/**
 * m3 echo Frontend - Tokens Tab
 *
 * Token usage tracking and cost analysis (moved from Settings).
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  TextField,
  MenuItem,
} from '@mui/material';
import { settingsApi } from '../../services/api';
import { tenantApi, TenantOption } from '../../services/admin';
import { useModelCatalog } from '../../hooks/useModelCatalog';

export const TokensTab: React.FC = () => {
  const { modelLabel } = useModelCatalog();
  const [apiKeys, setApiKeys] = useState<any>(null);
  const [showCosts, setShowCosts] = useState(false);
  const [modelPricing, setModelPricing] = useState<Record<string, { input: number; output: number }>>({});
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [tenantsLoading, setTenantsLoading] = useState(false);

  useEffect(() => {
    loadTenants();
    loadModelPricing();
  }, []);

  useEffect(() => {
    if (selectedTenantId) {
      loadApiKeys();
    }
  }, [selectedTenantId]);

  const loadTenants = async () => {
    try {
      setTenantsLoading(true);
      const response = await tenantApi.getOptions();
      setTenants(response.data);
      if (response.data.length > 0 && selectedTenantId === null) {
        setSelectedTenantId(response.data[0].id);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Mandanten:', error);
    } finally {
      setTenantsLoading(false);
    }
  };

  const loadApiKeys = async () => {
    try {
      const params = selectedTenantId ? { tenant_id: selectedTenantId } : {};
      const response = await settingsApi.getApiKeys(params);
      setApiKeys(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der API-Keys:', error);
    }
  };

  const loadModelPricing = async () => {
    try {
      const response = await settingsApi.getModelPricing();
      const pricingData = response.data;
      const pricingRecord: Record<string, { input: number; output: number }> = {};
      pricingData.forEach((p: any) => {
        pricingRecord[p.model_name] = {
          input: parseFloat(p.input_price_per_million) || 0,
          output: parseFloat(p.output_price_per_million) || 0,
        };
      });
      setModelPricing(pricingRecord);
    } catch (error) {
      console.error('Fehler beim Laden der Modell-Preise:', error);
    }
  };

  const calculateCost = (tokens: number, pricePerMillion: number): number => {
    return (tokens / 1000000) * pricePerMillion;
  };

  const formatTokenOrCost = (tokens: number, modelName: string, tokenType: 'prompt' | 'completion'): string => {
    if (!showCosts) {
      return tokens.toLocaleString();
    }
    const pricing = modelPricing[modelName];
    if (!pricing) {
      return '?';
    }
    const pricePerMillion = tokenType === 'prompt' ? pricing.input : pricing.output;
    if (pricePerMillion === 0) {
      return '?';
    }
    const cost = calculateCost(tokens, pricePerMillion);
    return `$${cost.toFixed(4)}`;
  };

  const formatTotalTokenOrCost = (promptTokens: number, completionTokens: number, modelName: string): string => {
    if (!showCosts) {
      const total = promptTokens + completionTokens;
      return total.toLocaleString();
    }
    const pricing = modelPricing[modelName];
    if (!pricing) {
      return '?';
    }
    if (pricing.input === 0 && pricing.output === 0) {
      return '?';
    }
    const promptCost = calculateCost(promptTokens, pricing.input);
    const completionCost = calculateCost(completionTokens, pricing.output);
    const totalCost = promptCost + completionCost;
    return `$${totalCost.toFixed(4)}`;
  };

  // Define all layers with their display order and names
  const layerDefinitions = [
    { keys: ['gemini_layer1', 'openai_layer1'], name: 'Layer 1 (Prompting)', order: 1 },
    { keys: ['gemini_layer2', 'openai_layer2'], name: 'Layer 2 (Analysis)', order: 2 },
    { keys: ['gemini_layer2_1', 'openai_layer2_1'], name: 'Layer 2.1 (Champion Consolidation)', order: 2.1 },
    { keys: ['gemini_layer4', 'openai_layer4'], name: 'Layer 4 (Insight Summarization)', order: 4 },
    { keys: ['gemini_layer5', 'openai_layer5'], name: 'Layer 5 (Optimierungsanalyse)', order: 5 },
    { keys: ['gemini_chatbot'], name: 'Chatbot', order: 10 },
    { keys: ['model_comparison'], name: 'Modellvergleich (Layer 2)', order: 11 },
  ];

  const getLayerName = (apiKeyId: string): string => {
    for (const layer of layerDefinitions) {
      if (layer.keys.includes(apiKeyId)) {
        return layer.name;
      }
    }
    return apiKeyId;
  };

  const getLayerOrder = (apiKeyId: string): number => {
    for (const layer of layerDefinitions) {
      if (layer.keys.includes(apiKeyId)) {
        return layer.order;
      }
    }
    return 999; // Unknown layers at the end
  };

  const formatModelName = (modelName: string): string => {
    const nameMap: Record<string, string> = {
      'gemini-2.5-flash': 'Gemini 2.5 Flash',
      'gemini-2.5-flash-lite': 'Gemini 2.5 Flash Lite',
      'gemini-2.5-pro': 'Gemini 2.5 Pro',
      'gpt-4o': 'GPT-4o',
      'gpt-4o-mini': 'GPT-4o Mini',
      'gpt-5.1': 'GPT 5.1',
      'gpt-5.1-chat-latest': 'GPT 5.1',
      'gpt-5.2': 'GPT 5.2',
      'gpt-5-pro': 'GPT 5 Pro',
    };
    // Katalog-Name zuerst, statische Map nur als Fallback fuer unbekannte Codes
    const label = modelLabel(modelName);
    return label !== modelName ? label : nameMap[modelName] || modelName;
  };

  return (
    <Box>
      <TextField
        select
        fullWidth
        label="Mandant auswählen"
        value={selectedTenantId || ''}
        onChange={(e) => setSelectedTenantId(Number(e.target.value))}
        disabled={tenantsLoading}
        helperText="Wählen Sie einen Mandanten aus, um dessen Token-Verbrauch anzuzeigen"
        sx={{ mb: 3 }}
      >
        {tenants.map((tenant) => (
          <MenuItem key={tenant.id} value={tenant.id}>
            {tenant.display_name}
          </MenuItem>
        ))}
      </TextField>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">
          Token-Verwendung nach Modell & Layer
        </Typography>
        <Button
          variant={showCosts ? "contained" : "outlined"}
          onClick={() => setShowCosts(!showCosts)}
          sx={{ minWidth: 120 }}
        >
          {showCosts ? 'Tokens anzeigen' : 'Kosten anzeigen'}
        </Button>
      </Box>

      {apiKeys && apiKeys.token_usage && apiKeys.token_usage.length > 0 ? (
        <Box>
          {(() => {
            const allModels = new Set<string>();
            apiKeys.token_usage.forEach((usage: any) => {
              usage.by_model.forEach((model: any) => {
                allModels.add(model.model_name);
              });
            });
            const modelsList = Array.from(allModels).sort();

            const layerGroups = apiKeys.token_usage
              .map((usage: any) => ({
                layerName: getLayerName(usage.api_key_identifier),
                apiKeyId: usage.api_key_identifier,
                models: usage.by_model,
                total: usage.total_tokens,
                order: getLayerOrder(usage.api_key_identifier),
              }))
              .sort((a: any, b: any) => a.order - b.order);

            return (
              <Box>
                {layerGroups.map((layer: any) => (
                  <Paper key={layer.apiKeyId} sx={{ mb: 3, p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      {layer.layerName}
                    </Typography>

                    <Box sx={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                            <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: 600 }}>
                              Token-Typ
                            </th>
                            {modelsList.map((modelName) => (
                              <th key={modelName} style={{ textAlign: 'right', padding: '12px 8px', fontWeight: 600 }}>
                                {formatModelName(modelName)}
                              </th>
                            ))}
                            <th style={{ textAlign: 'right', padding: '12px 8px', fontWeight: 600 }}>
                              Gesamt
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                            <td style={{ padding: '12px 8px' }}>{showCosts ? 'Input Kosten' : 'Prompt Tokens'}</td>
                            {modelsList.map((modelName) => {
                              const modelData = layer.models.find((m: any) => m.model_name === modelName);
                              return (
                                <td key={modelName} style={{ textAlign: 'right', padding: '12px 8px' }}>
                                  {modelData ? formatTokenOrCost(modelData.prompt_tokens, modelName, 'prompt') : '-'}
                                </td>
                              );
                            })}
                            <td style={{ textAlign: 'right', padding: '12px 8px', fontWeight: 600 }}>
                              {showCosts ? (
                                (() => {
                                  let totalCost = 0;
                                  let hasUnknown = false;
                                  layer.models.forEach((m: any) => {
                                    const pricing = modelPricing[m.model_name];
                                    if (!pricing || pricing.input === 0) {
                                      hasUnknown = true;
                                    } else {
                                      totalCost += calculateCost(m.prompt_tokens, pricing.input);
                                    }
                                  });
                                  return hasUnknown ? '?' : `$${totalCost.toFixed(4)}`;
                                })()
                              ) : (
                                layer.models.reduce((sum: number, m: any) => sum + m.prompt_tokens, 0).toLocaleString()
                              )}
                            </td>
                          </tr>

                          <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                            <td style={{ padding: '12px 8px' }}>{showCosts ? 'Output Kosten' : 'Completion Tokens'}</td>
                            {modelsList.map((modelName) => {
                              const modelData = layer.models.find((m: any) => m.model_name === modelName);
                              return (
                                <td key={modelName} style={{ textAlign: 'right', padding: '12px 8px' }}>
                                  {modelData ? formatTokenOrCost(modelData.completion_tokens, modelName, 'completion') : '-'}
                                </td>
                              );
                            })}
                            <td style={{ textAlign: 'right', padding: '12px 8px', fontWeight: 600 }}>
                              {showCosts ? (
                                (() => {
                                  let totalCost = 0;
                                  let hasUnknown = false;
                                  layer.models.forEach((m: any) => {
                                    const pricing = modelPricing[m.model_name];
                                    if (!pricing || pricing.output === 0) {
                                      hasUnknown = true;
                                    } else {
                                      totalCost += calculateCost(m.completion_tokens, pricing.output);
                                    }
                                  });
                                  return hasUnknown ? '?' : `$${totalCost.toFixed(4)}`;
                                })()
                              ) : (
                                layer.models.reduce((sum: number, m: any) => sum + m.completion_tokens, 0).toLocaleString()
                              )}
                            </td>
                          </tr>

                          <tr>
                            <td style={{ padding: '12px 8px', fontWeight: 600 }}>Gesamt</td>
                            {modelsList.map((modelName) => {
                              const modelData = layer.models.find((m: any) => m.model_name === modelName);
                              if (!modelData) {
                                return (
                                  <td key={modelName} style={{ textAlign: 'right', padding: '12px 8px', fontWeight: 600 }}>
                                    -
                                  </td>
                                );
                              }
                              return (
                                <td key={modelName} style={{ textAlign: 'right', padding: '12px 8px', fontWeight: 600 }}>
                                  {formatTotalTokenOrCost(modelData.prompt_tokens || 0, modelData.completion_tokens || 0, modelName)}
                                </td>
                              );
                            })}
                            <td style={{ textAlign: 'right', padding: '12px 8px', fontWeight: 700, fontSize: '1.1em' }}>
                              {showCosts ? (
                                (() => {
                                  let totalCost = 0;
                                  let hasUnknown = false;
                                  layer.models.forEach((m: any) => {
                                    const pricing = modelPricing[m.model_name];
                                    if (!pricing || (pricing.input === 0 && pricing.output === 0)) {
                                      hasUnknown = true;
                                    } else {
                                      totalCost += calculateCost(m.prompt_tokens || 0, pricing.input);
                                      totalCost += calculateCost(m.completion_tokens || 0, pricing.output);
                                    }
                                  });
                                  return hasUnknown ? '?' : `$${totalCost.toFixed(4)}`;
                                })()
                              ) : (
                                layer.models.reduce((sum: number, m: any) =>
                                  sum + (m.prompt_tokens || 0) + (m.completion_tokens || 0), 0
                                ).toLocaleString()
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </Box>
                  </Paper>
                ))}

                {/* Grand Total */}
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {showCosts ? 'Gesamtkosten (alle Layers)' : 'Gesamtverbrauch (alle Layers)'}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {showCosts ? (
                        (() => {
                          let totalCost = 0;
                          let hasUnknown = false;
                          apiKeys.token_usage.forEach((usage: any) => {
                            usage.by_model.forEach((model: any) => {
                              const pricing = modelPricing[model.model_name];
                              if (!pricing || (pricing.input === 0 && pricing.output === 0)) {
                                hasUnknown = true;
                              } else {
                                totalCost += calculateCost(model.prompt_tokens || 0, pricing.input);
                                totalCost += calculateCost(model.completion_tokens || 0, pricing.output);
                              }
                            });
                          });
                          return hasUnknown ? '?' : `$${totalCost.toFixed(2)}`;
                        })()
                      ) : (
                        `${apiKeys.token_usage.reduce((sum: number, u: any) =>
                          sum + (u.prompt_tokens || 0) + (u.completion_tokens || 0), 0
                        ).toLocaleString()} Tokens`
                      )}
                    </Typography>
                  </Box>

                  <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.3)', pt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.9 }}>
                      Breakdown nach Modell:
                    </Typography>
                    {(() => {
                      if (showCosts) {
                        const modelCosts: Record<string, { cost: number; hasUnknown: boolean }> = {};
                        apiKeys.token_usage.forEach((usage: any) => {
                          usage.by_model.forEach((model: any) => {
                            if (!modelCosts[model.model_name]) {
                              modelCosts[model.model_name] = { cost: 0, hasUnknown: false };
                            }
                            const pricing = modelPricing[model.model_name];
                            if (!pricing || (pricing.input === 0 && pricing.output === 0)) {
                              modelCosts[model.model_name].hasUnknown = true;
                            } else {
                              modelCosts[model.model_name].cost += calculateCost(model.prompt_tokens || 0, pricing.input);
                              modelCosts[model.model_name].cost += calculateCost(model.completion_tokens || 0, pricing.output);
                            }
                          });
                        });

                        const sortedModels = Object.entries(modelCosts).sort((a, b) => b[1].cost - a[1].cost);

                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {sortedModels.map(([modelName, data]) => (
                              <Box key={modelName} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                  {formatModelName(modelName)}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {data.hasUnknown ? '?' : `$${data.cost.toFixed(4)}`}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        );
                      } else {
                        const modelTotals: Record<string, number> = {};
                        apiKeys.token_usage.forEach((usage: any) => {
                          usage.by_model.forEach((model: any) => {
                            if (!modelTotals[model.model_name]) {
                              modelTotals[model.model_name] = 0;
                            }
                            modelTotals[model.model_name] += (model.prompt_tokens || 0) + (model.completion_tokens || 0);
                          });
                        });

                        const sortedModels = Object.entries(modelTotals).sort((a, b) => b[1] - a[1]);

                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {sortedModels.map(([modelName, total]) => (
                              <Box key={modelName} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                  {formatModelName(modelName)}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {total.toLocaleString()} Tokens
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        );
                      }
                    })()}
                  </Box>
                </Paper>
              </Box>
            );
          })()}
        </Box>
      ) : (
        <Alert severity="info">
          Keine Token-Verwendung bisher. Starten Sie einen Job, um Token-Tracking zu aktivieren.
        </Alert>
      )}
    </Box>
  );
};

export default TokensTab;
